import { useLocation } from 'wouter';
import { motion } from 'framer-motion';
import { useAccess } from '@/hooks/use-access';
import { SovCard } from '@/components/sovereign/ui/sov-card';
import { SovButton } from '@/components/sovereign/ui/sov-button';
import { WalletButton } from '@/components/sovereign/web3/wallet-button';

interface Props {
  children: React.ReactNode;
  requirePremium?: boolean;
}

export function ProtectedRoute({ children, requirePremium = false }: Props) {
  const { level, isConnected, isPremium } = useAccess();
  const [, navigate] = useLocation();

  if (level === 'full' && (!requirePremium || isPremium)) {
    return <>{children}</>;
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <SovCard variant="glass" padding="lg">
            <div className="text-center space-y-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-brand/10 border border-brand/30 mx-auto">
                <svg className="w-8 h-8 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Connect Your Wallet</h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Sign in with your main wallet to access the Dashboard and Calculator.
                </p>
              </div>

              <div className="flex justify-center">
                <WalletButton variant="primary" size="md" />
              </div>

              <SovButton fullWidth variant="outline" size="md" onClick={() => navigate('/')}>
                Back to Home
              </SovButton>
            </div>
          </SovCard>
        </motion.div>
      </div>
    );
  }

  if (requirePremium && !isPremium) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <SovCard variant="glass" padding="lg">
            <div className="text-center space-y-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-brand/10 border border-brand/30 mx-auto">
                <svg className="w-8 h-8 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Premium Feature</h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  This feature requires a premium subscription. Upgrade to unlock live dashboards, yield tools, and team management.
                </p>
              </div>

              <SovButton fullWidth variant="primary" size="md" onClick={() => navigate('/subscribe')}>
                Upgrade to Premium
              </SovButton>

              <SovButton fullWidth variant="outline" size="md" onClick={() => navigate('/')}>
                Back to Home
              </SovButton>
            </div>
          </SovCard>
        </motion.div>
      </div>
    );
  }

  return <>{children}</>;
}
